<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>

    <div v-else>
      <div class="element-box mx-auto" style="width: 80%;">
        <div class="row m-0 p-3 bg-white rounded shadow-sm"
             :style="{
              'border': `1px solid ${company?.color} !important`,
              'border-top': `3px solid ${company?.color} !important`
              }">
          <div class="col-12">
            <div class="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">

              <h5 class="fw-bold text-secondary mb-0">
                <i class="os-icon os-icon-user me-2" :style="{ color: company?.color }"></i>Benutzerdaten
              </h5>

              <div class="text-center px-3 mx-auto">
                <div class="text-danger fw-bold pulse-animation" style="font-size: 16px">
                  <i class="os-icon os-icon-clock me-1"></i>Link gültig bis: {{ tokenExpiresAt || 'Keine Angaben' }}
                </div>
              </div>

              <div>
                <img :src="company?.logo" alt="Logo" style="max-height: 50px; width: auto; object-fit: contain"/>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-md-6 mb-2">
                <small class="text-muted d-block text-uppercase fw-bold tracking-wider">Vor- und
                  Nachname</small>
                <span class="h5 fw-normal text-dark">
                {{ unemployed?.firstName }} {{ unemployed?.lastName }}
            </span>
              </div>
            </div>
            <div class="border-top pt-4">
              <h6 class="fw-bold text-secondary mb-3">
                <i class="os-icon os-icon-trending-up me-2" :style="{ color: company?.color }"></i>Coaching-Daten
              </h6>
              <div class="row">
                <div class="col-md-2 mb-3">
                  <small class="text-muted d-block text-uppercase fw-bold tracking-wider">Startdatum</small>
                  <span class="text-dark"> {{ coaching?.startDate || 'Keine Angaben' }} </span>
                </div>
                <div class="col-md-2 mb-3">
                  <small class="text-muted d-block text-uppercase fw-bold tracking-wider">Enddatum</small>
                  <span class="text-dark"> {{ coaching?.endDate || 'Keine Angaben' }} </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="table-responsive overflow-auto mt-5">
          <table class="table table-striped">
            <thead
                :style="{
                    borderBottom: `3px solid ${company?.color}`,
                    color: 'black'
                }"
              >
            <tr>
              <th class="py-2 px-2" style="font-size: 12px">Termin</th>
              <th class="py-2 px-2" style="font-size: 12px">Startzeit</th>
              <th class="py-2 px-2" style="font-size: 12px">Endzeit</th>
              <th class="py-2 px-2" style="font-size: 12px">Anwesenheit</th>
              <th class="text-end py-2 px-2" style="font-size: 12px">Einheiten</th>
              <th class="text-center py-2 px-2" style="font-size: 12px">Unterschrift</th>
              <th></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="meeting in meetings" :key="meeting.id">
              <th>{{ meeting?.termin }}</th>
              <td>{{ meeting?.startTime }}</td>
              <td>{{ meeting?.endTime }}</td>
              <td>{{ meeting?.presenceTranslation }}</td>
              <td class="text-end">{{ meeting?.units }}</td>
              <td class="text-center">
                <template v-if="!meeting.digitalSignature">
                  <span style="color: #cf1020"> Fehlende Digitale Unterschrift </span>
                </template>
                <template v-else>
                  <img style="min-width: 200px" height="60" :src="meeting.digitalSignature"/>
                </template>
              </td>
              <td class="text-end">
                <button class="btn btn-primary"
                        @click.stop="handleDigitalSignatureModal(
                            meeting?.id,
                            meeting.meetingType
                            )">
                  <i class="os-icon os-icon-edit"></i> Digitale Unterschrift
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <Modal
          v-model="showDigitalSignatureModal"
          title="Digitale Unterschrift"
          :hide-cancel="true"
          :button-label="$t('general.button.save')"
          sizeModal="modal-md"
          :hide-footer="true"
          classFooter="justify-content-center">
        <RemoteDigitalSignatureForm
            :token="token"
            :meetingId="meetingId"
            :meetingType="meetingType"
            v-model:submitForm="submitForm" />
      </Modal>
    </div>
  </PageTemplate>
</template>

<script setup lang="ts">
import portalCMCService from "@/api/Portal/CaseManagementCoaching";
import Loader from '@/components/Loader.vue';
import Modal from '@/components/modal/Modal.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import {CloseToast, ErrorToast} from '@/components/VueToast';
import {onMounted, ref, watch} from 'vue';
import {useRoute} from 'vue-router';
import RemoteDigitalSignatureForm
  from "@/views/App/Portal/CaseManagementCoaching/RemoteDigitalSignature/RemoteDigitalSignatureForm.vue";

const route = useRoute();

const company = ref<ICompany | null>(null);
const unemployed = ref<IUnemployed | null>(null);
const coaching = ref<ICoaching | null>(null);
const meetings = ref<IMeeting[]>([])
const meetingId = ref<number | null>(null);
const meetingType = ref<string>("")

const tokenExpiresAt = ref('')
const isLoaded = ref(false);
const showDigitalSignatureModal = ref(false);
const submitForm = ref(false);
const token = route.params.token as string;

interface ICompany {
  color: string;
  logo: string,
}

interface IUnemployed {
  firstName: string;
  lastName: string,
  address: string,
  city: string,
  zip: string,
  aaNumber: string,
  insuranceNumber: string,
  email: string,
  phone: string,
}

interface ICalculatedUnits {
  unitsDone: number;
  maxUnits: number;
}

interface ICoaching {
  startDate: string;
  endDate: string;
  calculatedUnits: ICalculatedUnits | null;
}

export interface IMeeting {
  id: number;
  termin: string;
  startTime: string;
  endTime: string;
  presenceTranslation: string;
  units: number;
  moduleName: string;
  digitalSignature: string | null;
  homeVisit: boolean;
  meetingType: string;
  coach?: {
    fullName: string;
  };
}

watch(submitForm, () => {
  meetingId.value = null;
  showDigitalSignatureModal.value = false;
  getData();
});

async function getData() {
  try {
    const {data} = await portalCMCService.getCmcRemoteMeetings(token);
    coaching.value = data.data?.coaching
    unemployed.value = data.data?.unemployed
    company.value = data.data?.company
    tokenExpiresAt.value = data.data?.expiresAt
    meetings.value = data.data?.meetings
  } catch (error) {
    await ErrorToast(error);
  }
}

function handleDigitalSignatureModal(id: number, type: string) {
  CloseToast();
  meetingId.value = id;
  meetingType.value = type;
  showDigitalSignatureModal.value = true;
}

onMounted(async () => {
  await getData()
  isLoaded.value = true;
});
</script>

<style scoped>

.pulse-animation {
  animation: pulse-effect 2s infinite ease-in-out;
  display: inline-block;
}

@keyframes pulse-effect {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.03);
    opacity: 0.85;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>